import { useState } from 'react';
import type { AuditDetail } from '../../stores/auditManagementStore';
import { Button } from '../ui/button';

const AUDIT_STATUS_BADGE_CLASSES: Record<string, string> = {
  success: 'text-green-400 bg-green-500/10 border-green-500/20',
  failure: 'text-red-400 bg-red-500/10 border-red-500/20',
  pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
};

const getStatusColor = (status: string) =>
  AUDIT_STATUS_BADGE_CLASSES[status] ??
  'text-neutral-400 bg-neutral-500/10 border-neutral-500/20';

interface AuditDetailModalProps {
  audit: AuditDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AuditDetailModal({
  audit,
  isOpen,
  onClose,
}: AuditDetailModalProps) {
  const [showChanges, setShowChanges] = useState(true);

  if (!isOpen || !audit) return null;

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatJson = (data: unknown) => {
    if (!data) return 'N/A';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-xl max-w-4xl w-full border border-neutral-800/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-neutral-900 px-6 pt-6 pb-4 border-b border-neutral-800/60">
          <h3 className="text-xl font-semibold text-white">
            Audit Log Details
          </h3>
          <p className="text-sm text-neutral-400 mt-1">
            Detailed information about this audit event
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Action
              </div>
              <div className="text-white font-mono text-sm bg-neutral-800/40 px-3 py-2 rounded-lg border border-neutral-800/60">
                {audit.action}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Status
              </div>
              <div
                className={`inline-flex px-3 py-2 rounded-lg text-sm font-medium border ${getStatusColor(audit.status)}`}
              >
                {audit.status.charAt(0).toUpperCase() + audit.status.slice(1)}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                User
              </div>
              <div className="text-white">
                {audit.user ? (
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {audit.user.fullName}
                    </div>
                    <div className="text-xs text-neutral-400">
                      {audit.user.email}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-neutral-400">System</span>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Timestamp
              </div>
              <div className="text-white text-sm">
                {new Date(audit.createdAt).toLocaleString('en-US', {
                  timeZone: 'America/New_York',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </div>
            </div>

            {audit.entityType && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                  Entity Type
                </div>
                <div className="text-white text-sm bg-neutral-800/40 px-3 py-2 rounded-lg border border-neutral-800/60">
                  {audit.entityType}
                </div>
              </div>
            )}

            {audit.entityId && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                  Entity ID
                </div>
                <div className="text-white font-mono text-xs bg-neutral-800/40 px-3 py-2 rounded-lg border border-neutral-800/60 break-all">
                  {audit.entityId}
                </div>
              </div>
            )}

            {audit.duration !== null && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                  Duration
                </div>
                <div className="text-white text-sm">
                  {formatDuration(audit.duration)}
                </div>
              </div>
            )}
          </div>

          {audit.error && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Error
              </div>
              <div className="text-red-400 bg-red-500/10 px-4 py-3 rounded-lg border border-red-500/20">
                {audit.error}
              </div>
            </div>
          )}

          {audit.metadata && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                Metadata
              </div>
              <div className="bg-neutral-800/40 rounded-lg p-4 border border-neutral-800/60">
                <pre className="text-sm text-white whitespace-pre-wrap font-mono overflow-x-auto">
                  {formatJson(audit.metadata)}
                </pre>
              </div>
            </div>
          )}

          {audit.changes && Object.keys(audit.changes).length > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                  Changes
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowChanges(true)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                      showChanges
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                        : 'bg-neutral-800/40 text-neutral-400 border border-neutral-800/60 hover:text-white'
                    }`}
                  >
                    JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowChanges(false)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                      !showChanges
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                        : 'bg-neutral-800/40 text-neutral-400 border border-neutral-800/60 hover:text-white'
                    }`}
                  >
                    Diff
                  </button>
                </div>
              </div>

              {showChanges ? (
                <div className="bg-neutral-800/40 rounded-lg p-4 border border-neutral-800/60">
                  <pre className="text-sm text-white whitespace-pre-wrap font-mono overflow-x-auto">
                    {formatJson(audit.changes)}
                  </pre>
                </div>
              ) : (
                <div className="bg-neutral-800/40 rounded-lg p-4 border border-neutral-800/60 space-y-3">
                  {Object.entries(audit.changes).map(([key, value]) => {
                    const changeValue = value as
                      | { old?: unknown; new?: unknown }
                      | unknown;
                    const isChangeObject =
                      typeof changeValue === 'object' &&
                      changeValue !== null &&
                      'old' in changeValue &&
                      'new' in changeValue;

                    return (
                      <div key={key} className="space-y-1">
                        <div className="text-sm font-medium text-neutral-300">
                          {key}
                        </div>
                        {isChangeObject ? (
                          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                            <div className="flex-1 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                              <div className="text-xs text-red-400 font-medium mb-1">
                                Before
                              </div>
                              <span className="text-sm text-red-300 font-mono line-through">
                                {formatJson(
                                  (
                                    changeValue as {
                                      old: unknown;
                                      new: unknown;
                                    }
                                  ).old,
                                )}
                              </span>
                            </div>
                            <div className="text-neutral-500 self-center">
                              →
                            </div>
                            <div className="flex-1 bg-green-500/5 border border-green-500/20 rounded-lg px-3 py-2">
                              <div className="text-xs text-green-400 font-medium mb-1">
                                After
                              </div>
                              <span className="text-sm text-green-300 font-mono">
                                {formatJson(
                                  (
                                    changeValue as {
                                      old: unknown;
                                      new: unknown;
                                    }
                                  ).new,
                                )}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-white text-sm bg-neutral-900/50 px-3 py-2 rounded-lg">
                            {formatJson(changeValue)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-neutral-800/60 space-y-1">
            <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
              Audit ID
            </div>
            <div className="text-white font-mono text-xs bg-neutral-800/40 px-3 py-2 rounded-lg border border-neutral-800/60 break-all">
              {audit.id}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-neutral-900 px-6 py-4 border-t border-neutral-800/60 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6 hover:bg-neutral-800/60 transition-colors"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
